interface EmbedCompletionData {
  payload: unknown;
  redirectUrl: string;
  summary?: string;
}

interface MarketoEmbedConfig {
  munchkinId: string;
  formId: number;
  fieldName: string;
  baseUrl?: string;
  submitTimeoutMs?: number;
  prefillFields?: Record<string, unknown>;
}

interface EmbedConfig {
  type: "journal-exposure";
  element: HTMLElement | null;
  version?: string;
  onComplete?: (data: EmbedCompletionData) => void;
  onReady?: () => void;
  onResize?: (height: number) => void;
  marketo?: MarketoEmbedConfig;
  options?: {
    baseUrl?: string;
    reportBaseUrl?: string;
  };
}

interface LoaderMessage {
  type: string;
  payload?: unknown;
}

type QueueItem = EmbedConfig | ((...args: unknown[]) => void);

const instances = new Map<HTMLElement, HTMLIFrameElement>();

// Per-instance Marketo form readiness promise
const marketoFormReadyMap = new Map<HTMLElement, Promise<MktoForm | null>>();

const DEFAULT_BASE_URL = (() => {
  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i -= 1) {
    const src = scripts[i].src;
    if (!src) continue;

    try {
      const url = new URL(src);
      if (url.pathname.endsWith("/embed.min.js") || url.pathname.endsWith("/embed.js")) {
        return url.origin + url.pathname.replace(/\/embed(\.min)?\.js$/, "");
      }
    } catch {
      continue;
    }
  }

  return "";
})();

// --- Marketo Forms 2.0 helpers ---

let marketoScriptLoaded: Promise<void> | null = null;

function loadMarketoForms2(baseUrl: string): Promise<void> {
  if ((window as Window).MktoForms2) {
    return Promise.resolve();
  }

  if (marketoScriptLoaded) {
    return marketoScriptLoaded;
  }

  marketoScriptLoaded = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${baseUrl}/js/forms2/js/forms2.min.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Marketo Forms 2.0"));
    document.head.appendChild(script);
  });

  return marketoScriptLoaded;
}

function createHiddenMarketoForm(baseUrl: string, munchkinId: string, formId: number): Promise<MktoForm> {
  return new Promise<MktoForm>((resolve, reject) => {
    const mkto = (window as Window).MktoForms2;
    if (!mkto) {
      reject(new Error("MktoForms2 not available"));
      return;
    }

    // Marketo injects fields into a <form id="mktoForm_XXXX"> element, not a div.
    // Position off-screen rather than display:none so Marketo's rendering doesn't bail.
    const container = document.createElement("form");
    container.id = `mktoForm_${formId}`;
    container.style.cssText = "position:absolute; left:-9999px; top:-9999px; width:1px; height:1px; overflow:hidden; visibility:hidden;";
    document.body.appendChild(container);

    try {
      mkto.loadForm(baseUrl, munchkinId, formId, (form) => {
        // Lock the form until we're ready to submit with populated values.
        form.submittable(false);
        resolve(form);
      });
    } catch (err) {
      reject(err);
    }
  });
}

function submitMarketoForm(form: MktoForm, fieldName: string, summary: string, prefillFields?: Record<string, unknown>): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    try {
      form.setValues({ ...prefillFields, [fieldName]: summary });

      form.onSuccess(() => {
        resolve();
        return false; // Prevent Marketo's default page redirect
      });

      form.submittable(true);
      form.submit();
    } catch (err) {
      reject(err);
    }
  });
}

// --- End Marketo helpers ---

function createIframe(config: EmbedConfig): HTMLIFrameElement | null {
  if (!config.element) {
    console.error("[RedwoodJournalAssessment] Missing container element");
    return null;
  }

  const existing = instances.get(config.element);
  if (existing) {
    existing.remove();
    instances.delete(config.element);
  }

  const baseUrl = config.options?.baseUrl || DEFAULT_BASE_URL;
  const version = config.version || "latest";
  const isDevVersion = version === "dev";
  const versionPath = version && version !== "latest" && version !== "dev" ? `v${version}` : version;

  const iframeUrl = new URL(
    isDevVersion ? "/" : `${baseUrl}/${versionPath}/index-embed.html`,
    isDevVersion ? window.location.origin : undefined
  );

  const parentParams = new URLSearchParams(window.location.search);
  const parentPayload = parentParams.get("payload");
  if (parentPayload) {
    iframeUrl.searchParams.set("payload", parentPayload);
  }
  iframeUrl.searchParams.set("embed", "true");
  iframeUrl.searchParams.set("type", config.type);
  if (config.options?.reportBaseUrl) {
    iframeUrl.searchParams.set("reportBaseUrl", config.options.reportBaseUrl);
  }

  const iframe = document.createElement("iframe");
  iframe.src = iframeUrl.toString();
  iframe.style.cssText = `
    width: 100%;
    min-height: 720px;
    border: none;
    display: block;
    overflow: hidden;
  `;
  iframe.setAttribute("allowfullscreen", "true");
  iframe.setAttribute("allow", "clipboard-write");

  config.element.innerHTML = "";
  config.element.appendChild(iframe);
  instances.set(config.element, iframe);
  return iframe;
}

function handleMessage(event: MessageEvent, config: EmbedConfig, iframe: HTMLIFrameElement) {
  if (event.source !== iframe.contentWindow) {
    return;
  }

  const data = event.data as LoaderMessage;
  if (!data?.type || !data.type.startsWith("redwood:assessment:")) {
    return;
  }

  switch (data.type) {
    case "redwood:assessment:ready":
      config.onReady?.();
      break;
    case "redwood:assessment:resize": {
      const payload = data.payload as { height?: number };
      if (payload?.height && payload.height > 0) {
        iframe.style.height = `${payload.height}px`;
        config.onResize?.(payload.height);
      }
      break;
    }
    case "redwood:assessment:complete": {
      const payload = data.payload as EmbedCompletionData;
      config.onComplete?.(payload);

      const redirectUrl = payload?.redirectUrl;
      const marketoFormReady = config.element ? marketoFormReadyMap.get(config.element) : null;

      if (config.marketo && payload?.summary && marketoFormReady) {
        const timeoutMs = config.marketo.submitTimeoutMs ?? 15000;
        const { fieldName, prefillFields } = config.marketo;

        const submitPromise = marketoFormReady.then((form) => {
          if (!form) throw new Error("Marketo form not loaded");
          return submitMarketoForm(form, fieldName, payload.summary!, prefillFields);
        });

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Marketo submit timeout")), timeoutMs)
        );

        Promise.race([submitPromise, timeoutPromise])
          .catch((err) => {
            console.warn("[RedwoodJournalAssessment] Marketo submission failed:", err);
          })
          .finally(() => {
            if (redirectUrl) window.location.href = redirectUrl;
          });
      } else {
        if (redirectUrl) window.location.href = redirectUrl;
      }
      break;
    }
    default:
      break;
  }
}

function initAssessment(config: EmbedConfig): void {
  const iframe = createIframe(config);
  if (!iframe) {
    return;
  }

  // Eagerly load Marketo form while user answers questions
  if (config.marketo && config.element) {
    const { munchkinId, formId } = config.marketo;
    // Redwood's Marketo instance is hosted on one.redwood.com, not the generic
    // {munchkinId}.mktoweb.com. Callers can override via config.marketo.baseUrl.
    const baseUrl = config.marketo.baseUrl || "//one.redwood.com";
    const formReady = loadMarketoForms2(baseUrl)
      .then(() => createHiddenMarketoForm(baseUrl, munchkinId, formId))
      .catch((err) => {
        console.warn("[RedwoodJournalAssessment] Marketo form pre-load failed:", err);
        return null;
      });
    marketoFormReadyMap.set(config.element, formReady);
  }

  const messageHandler = (event: MessageEvent) => handleMessage(event, config, iframe);
  window.addEventListener("message", messageHandler);

  const observer = new MutationObserver(() => {
    if (!document.contains(iframe)) {
      window.removeEventListener("message", messageHandler);
      observer.disconnect();
      if (config.element && instances.get(config.element) === iframe) {
        instances.delete(config.element);
        marketoFormReadyMap.delete(config.element);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function processQueue(queue: QueueItem[]) {
  queue.forEach((item) => {
    if (typeof item === "function") {
      item();
    } else {
      initAssessment(item);
    }
  });
}

function init() {
  const globalWindow = window as unknown as {
    RedwoodJournalAssessment?: QueueItem[];
  };
  const existingQueue = globalWindow.RedwoodJournalAssessment;

  const api = {
    push(config: QueueItem) {
      if (typeof config === "function") {
        config();
      } else if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => initAssessment(config));
      } else {
        initAssessment(config);
      }
    },
    init(config: EmbedConfig) {
      this.push(config);
    },
    destroy(element: HTMLElement) {
      const iframe = instances.get(element);
      if (iframe) {
        iframe.remove();
        instances.delete(element);
      }
      marketoFormReadyMap.delete(element);
    },
    version: "1.0.0"
  };

  (window as unknown as { RedwoodJournalAssessment: typeof api }).RedwoodJournalAssessment = api;

  if (Array.isArray(existingQueue)) {
    processQueue(existingQueue);
  }
}

if (typeof window !== "undefined") {
  init();
}

export { init };
export type { EmbedConfig, EmbedCompletionData, MarketoEmbedConfig };
